import { Button, Center, Flex, HStack, RadioCard, Spinner, Text, VStack } from '@chakra-ui/react';
import { ugcBriefGaps, type BriefTemplateResponse } from '@fluenceur/types';
import { InfoCircle, Page, PageEdit, Sparks } from 'iconoir-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInvitationActions, useInvitationStoreSelector } from '@/contexts/invitation-context';
import { useBriefTemplatesQuery } from '@/hooks/queries/use-brief-templates-query';
import { briefTypeOfWizard } from '../collab-types';
import {
  PLATFORM_LABEL,
  deliverableLabel,
} from '@/pages/collabs/brief-templates/_shared/brief-summary-lines';
import { briefCompensationSummary } from '@/pages/collabs/brief-templates/_shared/use-compensation-summary';
import { CollabBriefOptionCard } from './collab-brief-option-card';
import {
  CollabLegalIdentityBanner,
  useBrandLegalIdentityIncomplete,
} from './collab-legal-identity-banner';
import { emptyCollabContent, hasDeliberateCompensation } from './collab-wizard-helpers';
import { useCollabRecipients } from './use-collab-recipients';

const BLANK_OPTION = '__blank__';

interface CollabBriefStepProps {
  onClose: () => void;
}

const isAiDraft = (template: BriefTemplateResponse): boolean =>
  Boolean(template.content.concept.aiGenerated || template.content.guidelines.aiGenerated);

/**
 * A UGC brief the send would refuse, by the very rule it refuses on: one
 * started in the library and left without its price, its rights or its terms.
 */
const isIncompleteUgcBrief = (template: BriefTemplateResponse): boolean =>
  ugcBriefGaps(template.content).length > 0;

/**
 * Brief step of the wizard, one for every collab type: pick a brief of the
 * chosen type from the library, or start a blank sponsored brief. Continuing
 * with a template seeds its content and jumps straight to the terms; the blank
 * option routes through the draft step first. A UGC proposal is always built
 * on a UGC brief, whose delivery terms and rights are written in the editor, so
 * the UGC path offers no blank brief.
 */
const CollabBriefStep = ({ onClose }: CollabBriefStepProps) => {
  const { t, i18n } = useTranslation();
  const collabTemplateId = useInvitationStoreSelector((state) => state.collabTemplateId);
  const collabBriefMode = useInvitationStoreSelector((state) => state.collabBriefMode);
  const collabType = useInvitationStoreSelector((state) => state.collabType);
  const isUgc = collabType === 'ugc';
  const { setCollabBrief, setCurrentView } = useInvitationActions();
  const recipients = useCollabRecipients();

  // Each path lists the briefs of its own type only: a sponsored proposal
  // cannot stand in for a UGC one, nor the other way round.
  const { data: templates = [], isLoading } = useBriefTemplatesQuery({
    collabType: briefTypeOfWizard(collabType),
  });

  // Said here, at the entrance, rather than only at the send five steps later:
  // a brand that learns it at the last screen has already written the whole
  // brief. Nothing is blocked yet — a collab without a counterpart never needs
  // this — so it warns and leaves the tunnel open.
  const legalIdentityIncomplete = useBrandLegalIdentityIncomplete();

  const [picked, setPicked] = useState<string | null>(
    collabBriefMode === 'blank' ? BLANK_OPTION : (collabTemplateId ?? null)
  );
  // A preselected brief this path does not list (another type, an archived or
  // deleted one) is dropped rather than kept as a choice the CTA cannot use:
  // the brand picks again among the briefs actually offered.
  // A UGC brief the send would refuse is listed but cannot be picked.
  const selectable = (template: BriefTemplateResponse) => !isUgc || !isIncompleteUgcBrief(template);
  const selection =
    (picked === BLANK_OPTION && !isUgc) ||
    templates.some((template) => template.id === picked && selectable(template))
      ? picked
      : null;

  const templateMeta = (template: BriefTemplateResponse): string => {
    const { content } = template;
    const deliverables = content.deliverables
      .map((d) => `${d.quantity} × ${deliverableLabel(d, t)}`)
      .join(' + ');
    const compensation = briefCompensationSummary(content, t, i18n.language);
    const platforms = [
      ...new Set(content.deliverables.map((d) => (d.platform ? PLATFORM_LABEL[d.platform] : null))),
    ]
      .filter(Boolean)
      .join(' + ');
    return [deliverables, compensation, platforms].filter(Boolean).join(' · ');
  };

  // The title outlives the wizard (creator page, notifications, the list): a
  // prospect the plan masks has no name to put in it, so it keeps the plain one.
  const singleName = recipients.length === 1 ? recipients[0].displayName.trim() : '';
  const blankBriefName = singleName
    ? t('invitations.modal.collab.wizard.brief.blankNameFor', { name: singleName })
    : t('invitations.modal.collab.wizard.brief.blankName');

  const handleContinue = () => {
    if (!selection) return;
    if (selection === BLANK_OPTION) {
      setCollabBrief({
        mode: 'blank',
        templateId: null,
        name: blankBriefName,
        content: emptyCollabContent(
          t('collabs.briefTemplates.sections.guidelines.legal.placeholder')
        ),
        compensationConfirmed: false,
      });
      setCurrentView('collabDraft');
      return;
    }
    const template = templates.find((entry) => entry.id === selection);
    if (!template) return;
    setCollabBrief({
      mode: 'template',
      templateId: template.id,
      name: template.name,
      content: template.content,
      // A template already carrying a deliberate compensation counts as an
      // explicit choice; a bare 'none' must be re-confirmed on the terms step.
      compensationConfirmed: hasDeliberateCompensation(template.content),
    });
    setCurrentView('collabTerms');
  };

  if (isLoading) {
    return (
      <Center py="10">
        <Spinner size="md" color="primary.500" />
      </Center>
    );
  }

  return (
    <Flex direction="column" gap="4">
      {legalIdentityIncomplete && <CollabLegalIdentityBanner variant="early" />}

      <RadioCard.Root
        value={selection ?? ''}
        onValueChange={(details) => {
          if (details.value) setPicked(details.value);
        }}
        colorPalette="primary"
      >
        <VStack align="stretch" gap="2">
          {templates.map((template) => (
            <CollabBriefOptionCard
              key={template.id}
              value={template.id}
              icon={isAiDraft(template) ? Sparks : Page}
              title={template.name}
              meta={
                selectable(template)
                  ? templateMeta(template)
                  : t('invitations.modal.collab.wizard.brief.ugcIncomplete')
              }
              isAiDraft={isAiDraft(template)}
              disabled={!selectable(template)}
            />
          ))}
          {!isUgc && (
            <CollabBriefOptionCard
              value={BLANK_OPTION}
              icon={PageEdit}
              title={t('invitations.modal.collab.wizard.brief.blankTitle')}
              meta={t('invitations.modal.collab.wizard.brief.blankMeta')}
            />
          )}
        </VStack>
      </RadioCard.Root>

      {isUgc && templates.length === 0 && (
        <Text fontSize="12.5px" color="fg.muted" lineHeight="1.5">
          {t('invitations.modal.collab.wizard.brief.ugcEmpty')}
        </Text>
      )}

      <HStack gap="2" color="fg.muted" align="flex-start">
        <InfoCircle width={14} height={14} style={{ flexShrink: 0, marginTop: 2 }} />
        <Text fontSize="12px" lineHeight="1.5">
          {t('invitations.modal.collab.wizard.brief.freezeNote')}
        </Text>
      </HStack>

      <Flex justify="flex-end" gap="2" pt="1">
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button colorPalette="primary" onClick={handleContinue} disabled={!selection}>
          {selection === BLANK_OPTION
            ? t('invitations.modal.collab.wizard.brief.ctaBlank')
            : t('invitations.modal.collab.wizard.brief.ctaTemplate')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default CollabBriefStep;
